import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  Briefcase,
  Building2,
  CalendarDays,
  Compass,
  Cpu,
  Handshake,
  Home,
  Landmark,
  Play,
  Radio,
  Shield,
  Timer,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { hashHref } from "../../constants/site";
import {
  APPOINTMENT_TYPES,
  CAPABILITY_GROUPS,
  DIFFERENTIATORS,
  INDUSTRIES,
  INSTITUTION_TYPES,
  INTEREST_AREAS,
  MARKET_FAQS,
  MARKET_PORTAL,
  RELATED,
  VIDEO_MEDIA,
  VIDEOS,
} from "../../constants/simmarkets";
import { ArrowIcon, CornerMarks, Field, MediaSlot, SectionShell, useFade, useKnockoutLogo } from "./ui";

const INDUSTRY_ICONS = {
  landmark: Landmark,
  building: Building2,
  wallet: Wallet,
  users: Users,
  shield: Shield,
  trend: TrendingUp,
  clock: Timer,
  home: Home,
  cpu: Cpu,
  radio: Radio,
  briefcase: Briefcase,
  compass: Compass,
};

const INTENTS = [
  {
    id: "demo",
    title: "Request a live demo",
    copy: "See how SimMarket can transform your customer acquisition strategy and improve consumer engagement.",
    cta: "Book my demo",
  },
  {
    id: "sales",
    title: "Contact sales",
    copy: "Whether you're a bank, insurance company, FinTech or financial institution, our specialists are ready to help.",
    cta: "Talk to sales",
  },
  {
    id: "appointment",
    title: "Book an appointment",
    copy: "Schedule a one-on-one consultation to discuss customer acquisition, digital engagement and marketplace requirements.",
    cta: "Schedule appointment",
  },
];

const NEXT_STEPS = [
  {
    n: "01",
    title: "Become a Partner",
    copy: "List products and receive qualified, intent-driven leads.",
    href: hashHref("partner"),
    icon: Handshake,
  },
  {
    n: "02",
    title: "Talk to Sales",
    copy: "Speak with a specialist about your acquisition goals.",
    href: "#sales",
    icon: Users,
  },
  {
    n: "03",
    title: "Book a Consultation",
    copy: "Schedule a working session around marketplace deployment.",
    href: "#appointment",
    icon: CalendarDays,
  },
  {
    n: "04",
    title: "Watch the Product Demo",
    copy: "See the engines, journey and partner dashboard in motion.",
    href: "#videos",
    icon: Play,
  },
];

const INITIAL = {
  name: "",
  company: "",
  title: "",
  institution: "",
  email: "",
  phone: "",
  date: "",
  appointment: APPOINTMENT_TYPES[0],
};

function MarketClose() {
  const { fade, reduce } = useFade();
  const [video, setVideo] = useState(0);
  const [openFaq, setOpenFaq] = useState(MARKET_FAQS[0]?.n || "");
  const [intent, setIntent] = useState("demo");
  const [form, setForm] = useState(INITIAL);
  const [interests, setInterests] = useState([]);
  const [sent, setSent] = useState(false);
  const activeVideo = VIDEOS[video];
  const activeIntent = INTENTS.find((item) => item.id === intent) || INTENTS[0];

  useEffect(() => {
    const apply = () => {
      const id = window.location.hash.replace("#", "");
      if (id === "sales" || id === "appointment" || id === "demo") setIntent(id);
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (sent) setSent(false);
  };

  const toggleInterest = (item) => {
    setInterests((current) =>
      current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]
    );
    if (sent) setSent(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSent(true);
  };

  return (
    <>
      <SectionShell id="platform" labelledBy="platform-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Industries &amp; technology
            </p>
            <h2
              id="platform-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Built for the financial sector.
              <span className="mt-1 block">Ready for enterprise deployment.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              From rural banks to national insurers, SimMarket deploys as one marketplace stack —
              education, intelligence, acquisition and care — across the institutions that need it.
            </p>
          </motion.header>

          <motion.div
            className="relative mt-12 grid overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <div className="bg-white px-6 py-8 sm:px-8 sm:py-10">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-market-green">
                    Industries served
                  </p>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-navy/55">
                    One marketplace, twelve kinds of financial institution.
                  </p>
                </div>
                <p className="font-mono text-[11px] tracking-[0.18em] text-market-gold">
                  {String(INDUSTRIES.length).padStart(2, "0")}
                </p>
              </div>
              <ul className="mt-7 grid gap-2 sm:grid-cols-2">
                {INDUSTRIES.map((item, index) => {
                  const Icon = INDUSTRY_ICONS[item.icon] || Landmark;
                  return (
                    <li
                      key={item.name}
                      className="flex items-center gap-3 border-l-[3px] border-market-gold bg-[#f3f7f5] px-3 py-3"
                    >
                      <span className="font-mono text-[11px] tracking-[0.14em] text-market-green">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <Icon className="h-4 w-4 shrink-0 text-market-gold" strokeWidth={1.75} aria-hidden="true" />
                      <span className="text-sm font-semibold leading-snug tracking-tight text-navy">
                        {item.name}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="bg-navy px-6 py-8 text-white sm:px-8 sm:py-10">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-market-gold">
                    Core capabilities
                  </p>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/60">
                    The operating stack behind every marketplace decision.
                  </p>
                </div>
                <p className="font-mono text-[11px] tracking-[0.18em] text-market-gold">
                  {String(CAPABILITY_GROUPS.reduce((count, group) => count + group.items.length, 0)).padStart(2, "0")}
                </p>
              </div>
              <ul className="mt-7 grid gap-6 sm:grid-cols-2">
                {CAPABILITY_GROUPS.map((group) => (
                  <li key={group.title}>
                    <p className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] text-market-gold">
                      {group.index}
                      <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                        {group.title}
                      </span>
                    </p>
                    <ul className="mt-3 space-y-2">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm leading-snug text-white/80">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-market-gold" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            className="mt-6 border border-navy/10 bg-white p-6 sm:p-8"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40">
              Why choose SimMarket?
            </p>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-navy/65 sm:text-lg">
              Unlike traditional financial comparison websites, SimMarket combines education,
              intelligence and acquisition into one ecosystem. We don&apos;t simply generate traffic.
              We generate informed customers.
            </p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {DIFFERENTIATORS.map((item) => (
                <li
                  key={item}
                  className="border border-navy/10 bg-[#f3f7f5] px-3 py-3 text-sm font-semibold leading-snug tracking-tight text-navy"
                >
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </SectionShell>

      <SectionShell id="videos" labelledBy="videos-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Watch SimMarket in action
            </p>
            <h2
              id="videos-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Product demonstration
              <span className="mt-1 block">&amp; video tutorials</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Discover how SimMarket transforms financial customer acquisition through our
              interactive demonstrations.
            </p>
          </motion.header>

          <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="relative overflow-hidden border border-navy/10 bg-navy">
              <div className="relative aspect-video">
                <MediaSlot
                  src={VIDEO_MEDIA.src}
                  alt={VIDEO_MEDIA.alt}
                  label={activeVideo.title}
                  spec={VIDEO_MEDIA.spec}
                  className="absolute inset-0 h-full w-full"
                />
                <div className="absolute inset-0 bg-[#16352e]/45" />
                <button
                  type="button"
                  className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold"
                  aria-label={`Play ${activeVideo.title}`}
                >
                  <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-market-gold text-navy">
                    <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold">{activeVideo.title}</span>
                </button>
              </div>
             
            </div>
            <ul className="max-h-[28rem] overflow-y-auto border border-navy/10 bg-white">
              {VIDEOS.map((item, index) => {
                const selected = index === video;
                return (
                  <li key={item.id} className={index > 0 ? "border-t border-navy/10" : ""}>
                    <button
                      type="button"
                      onClick={() => setVideo(index)}
                      className={`flex w-full min-h-14 cursor-pointer items-start justify-between gap-3 px-5 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold ${
                        selected ? "bg-[#f3f7f5]" : "hover:bg-[#f3f7f5]/70"
                      }`}
                    >
                      <span>
                        <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-market-green">
                          {item.kind}
                        </span>
                        <span className="mt-1 block text-sm font-semibold tracking-tight text-navy">
                          {item.title}
                        </span>
                      </span>
                      {selected ? <Play className="mt-1 h-4 w-4 shrink-0 text-market-gold" aria-hidden="true" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="faqs" labelledBy="faqs-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="flex max-w-3xl flex-col gap-5 sm:flex-row sm:items-end sm:justify-between"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                Frequently asked questions
              </p>
              <h2
                id="faqs-heading"
                className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
              >
                Answers from the
                <span className="mt-1 block">SimMarket library</span>
              </h2>
            </div>
            <Link
              to="/faqs#topic-simmarkets"
              className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 text-sm font-semibold text-market-green transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
            >
              View all FAQs
              <ArrowIcon className="h-3.5 w-3.5" />
            </Link>
          </motion.header>

          <div className="relative mt-12">
            <CornerMarks />
            <div className="relative overflow-hidden border border-navy/10 bg-white">
              {MARKET_FAQS.map((item) => {
                const isOpen = openFaq === item.n;
                const panelId = `market-faq-${item.n}`;
                return (
                  <div key={item.n} className="border-b border-navy/10 last:border-b-0">
                    <h3>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setOpenFaq(isOpen ? "" : item.n)}
                        className={`flex w-full cursor-pointer items-start gap-4 px-5 py-5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold sm:px-6 ${
                          isOpen ? "bg-[#f3f7f5]" : "hover:bg-[#f3f7f5]"
                        }`}
                      >
                        <span className="mt-0.5 shrink-0 font-mono text-[11px] font-semibold tracking-[0.16em] text-market-green">
                          {item.n}
                        </span>
                        <span className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-tight text-navy sm:text-lg">
                          {item.question}
                        </span>
                        <span
                          aria-hidden="true"
                          className={`relative mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border ${
                            isOpen ? "border-market-gold bg-market-gold text-navy" : "border-navy/15"
                          }`}
                        >
                          <span className="absolute h-px w-3 bg-current" />
                          <span
                            className={`absolute h-3 w-px bg-current transition ${isOpen ? "scale-y-0" : "scale-y-100"}`}
                          />
                        </span>
                      </button>
                    </h3>
                    <motion.div
                      id={panelId}
                      role="region"
                      initial={false}
                      animate={
                        reduce
                          ? { height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }
                          : isOpen
                            ? { height: "auto", opacity: 1 }
                            : { height: 0, opacity: 0 }
                      }
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-6 pl-[4.5rem] text-sm leading-relaxed text-navy/60 sm:px-6 sm:pl-[5.75rem]">
                        {item.answer ? <p>{item.answer}</p> : null}
                        {item.bullets ? (
                          <ul className="mt-3 list-disc space-y-1 pl-5">
                            {item.bullets.map((bullet) => (
                              <li key={bullet}>{bullet}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="demo" labelledBy="demo-heading" tone="white">
        <div id="sales" className="scroll-mt-32" />
        <div id="appointment" className="scroll-mt-32" />
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Request a live demo
            </p>
            <h2
              id="demo-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Experience SimMarket
              <span className="mt-1 block text-market-green">in action</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              {activeIntent.copy}
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="flex h-full flex-col bg-[#f3f7f5] p-6 sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/40">
                How should we help?
              </p>
              <ul className="mt-4 flex flex-1 flex-col gap-2">
                {INTENTS.map((item, index) => {
                  const selected = item.id === intent;
                  return (
                    <li key={item.id} className="flex min-h-0 flex-1">
                      <button
                        type="button"
                        onClick={() => setIntent(item.id)}
                        className={`flex h-full min-h-16 w-full cursor-pointer flex-col items-start justify-center border px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                          selected
                            ? "border-market-green bg-market-green text-white"
                            : "border-navy/10 bg-white text-navy hover:border-market-green/40"
                        }`}
                      >
                        <span className={`font-mono text-[11px] tracking-[0.16em] ${selected ? "text-white/70" : "text-market-green"}`}>
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="mt-2 text-sm font-semibold tracking-tight">{item.title}</span>
                        <span className={`mt-1 text-xs leading-relaxed ${selected ? "text-white/75" : "text-navy/50"}`}>
                          {item.copy}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {intent === "sales" ? (
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  {["Call our sales team", "Email sales", "WhatsApp Business", "Live chat"].map((item) => (
                    <li
                      key={item}
                      className="border border-navy/10 bg-white px-3 py-3 text-sm font-semibold text-navy/80"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-6 border-t border-navy/10 pt-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-market-green">
                  Typical reply
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-navy/55">
                  A SimMarket specialist usually responds within one business day.
                </p>
              </div>
            </div>

            <form className="flex h-full flex-col bg-white px-6 py-8 sm:px-8 sm:py-10" onSubmit={onSubmit} noValidate>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Full name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  autoComplete="name"
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  required
                  autoComplete="organization"
                />
                <Field label="Job title" name="title" value={form.title} onChange={onChange} autoComplete="organization-title" />
                <div>
                  <label
                    htmlFor="institution"
                    className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
                  >
                    Institution <span className="text-market-gold">*</span>
                  </label>
                  <select
                    id="institution"
                    name="institution"
                    value={form.institution}
                    onChange={onChange}
                    required
                    className="mt-2 min-h-12 w-full cursor-pointer border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition focus:border-market-gold focus:ring-2 focus:ring-market-gold/40"
                  >
                    <option value="" disabled>
                      Select institution type
                    </option>
                    {INSTITUTION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <Field
                  label="Email address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  autoComplete="email"
                />
                <Field
                  label="Phone number"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  autoComplete="tel"
                />
                <Field label="Preferred demo date" name="date" type="date" value={form.date} onChange={onChange} />
                {intent === "appointment" ? (
                  <div>
                    <label
                      htmlFor="appointment"
                      className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
                    >
                      Appointment type
                    </label>
                    <select
                      id="appointment"
                      name="appointment"
                      value={form.appointment}
                      onChange={onChange}
                      className="mt-2 min-h-12 w-full cursor-pointer border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition focus:border-market-gold focus:ring-2 focus:ring-market-gold/40"
                    >
                      {APPOINTMENT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div />
                )}
                <div className="sm:col-span-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40">
                    Areas of interest
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {INTEREST_AREAS.map((item) => {
                      const on = interests.includes(item);
                      return (
                        <li key={item}>
                          <button
                            type="button"
                            onClick={() => toggleInterest(item)}
                            aria-pressed={on}
                            className={`inline-flex min-h-10 cursor-pointer items-center rounded-full px-3 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                              on
                                ? "bg-market-green text-white"
                                : "border border-navy/15 bg-[#f3f7f5] text-navy/70 hover:border-navy/40"
                            }`}
                          >
                            {item}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="mt-auto pt-8">
                <button
                  type="submit"
                  className="inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full bg-market-gold px-6 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold focus-visible:ring-offset-2"
                >
                  {activeIntent.cta}
                  <ArrowIcon />
                </button>
                {sent ? (
                  <p className="mt-4 text-sm text-navy/55" role="status">
                    Details captured. A SimMarket specialist will follow up to confirm your{" "}
                    {intent === "appointment" ? "appointment" : "demonstration"}.
                  </p>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="related" labelledBy="related-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Related solutions
            </p>
            <h2
              id="related-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Your customer journey
              <span className="mt-1 block">doesn&apos;t end with acquisition</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Explore the other intelligent solutions within the SimFinance ecosystem.
            </p>
          </motion.header>

          <ul className="mt-12 grid gap-6 lg:grid-cols-2">
            {RELATED.map((item) => (
              <li key={item.id} className="relative overflow-hidden border border-navy/10 bg-white">
                <RelatedCard item={item} />
              </li>
            ))}
          </ul>
        </div>
      </SectionShell>

      <SectionShell id="ready" labelledBy="ready-heading" tone="green">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-market-gold to-transparent"
            aria-hidden="true"
          />
          <p
            className="pointer-events-none absolute -left-2 top-10 select-none text-[7rem] font-black leading-none tracking-tight text-white/[0.06] sm:text-[10rem] lg:top-6 lg:text-[12rem]"
            aria-hidden="true"
          >
            READY
          </p>

          <motion.div
            className="relative border border-white/15 bg-white/[0.06] p-6 sm:p-10 lg:p-12"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <CornerMarks dark />
            <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-14">
              <div className="flex flex-col">
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-market-gold">
                  Next step
                </p>
                <h2
                  id="ready-heading"
                  className="mt-4 font-semibold leading-[1.08] tracking-tight [font-size:clamp(2rem,5vw,3.75rem)]"
                >
                  Ready to transform
                  <span className="mt-1 block">customer acquisition?</span>
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
                  Empower consumers with trusted financial education, intelligent recommendations and
                  transparent product comparisons while delivering qualified, high-intent customers
                  directly to your organization.
                </p>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
                  Whether you&apos;re helping customers make better financial decisions or seeking a
                  smarter way to acquire and engage new clients, SimMarket is the intelligent
                  marketplace built for Africa&apos;s financial future.
                </p>
                <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row sm:flex-wrap">
                  <a
                    href="#demo"
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center rounded-full bg-market-gold px-7 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    Request a Demo
                  </a>
                  <a
                    href={MARKET_PORTAL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center rounded-full bg-white px-7 text-sm font-semibold text-navy transition hover:bg-market-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    Open the marketplace
                  </a>
                </div>
              </div>

              <ol className="flex flex-col overflow-hidden border border-white/15 bg-navy/25" aria-label="More ways to get started">
                {NEXT_STEPS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.n} className="border-t border-white/10 first:border-t-0">
                      <a
                        href={item.href}
                        className="group flex min-h-[5.5rem] cursor-pointer items-start gap-4 px-5 py-5 transition hover:bg-white/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold"
                      >
                        <span className="font-mono text-[11px] tracking-[0.18em] text-market-gold">
                          {item.n}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 text-sm font-semibold tracking-tight text-white">
                            <Icon className="h-4 w-4 shrink-0 text-market-gold" strokeWidth={1.75} aria-hidden="true" />
                            {item.title}
                          </span>
                          <span className="mt-1 block text-sm leading-relaxed text-white/65">{item.copy}</span>
                        </span>
                        <ArrowIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-market-gold transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </motion.div>
        </div>
      </SectionShell>
    </>
  );
}

function RelatedCard({ item }) {
  const { href: logoHref, failed } = useKnockoutLogo(item.logo);
  const logo = !failed && logoHref ? logoHref : item.logo;
  const destination = item.href;
  const interactive = Boolean(destination);
  const isExternal = Boolean(destination?.startsWith("http"));

  const inner = (
    <>
      <div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-market-green">{item.index}</p>
        {logo ? (
          <img src={logo} alt="" className="mt-4 h-10 w-auto max-w-[12rem] object-contain object-left" />
        ) : null}
        <h3 className="mt-4 text-2xl font-semibold tracking-tight text-navy">{item.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-navy/60 sm:text-base">{item.copy}</p>
      </div>
      <span className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold text-market-green ${interactive ? "group-hover:text-navy" : ""}`}>
        {item.cta}
        {interactive ? (
          <ArrowIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        ) : null}
      </span>
    </>
  );

  const className = interactive
    ? "group flex h-full min-h-[16rem] cursor-pointer flex-col justify-between p-6 transition hover:bg-[#f3f7f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold sm:p-8"
    : "flex h-full min-h-[16rem] flex-col justify-between p-6 sm:p-8";

  if (!interactive) {
    return <div className={className}>{inner}</div>;
  }

  if (isExternal) {
    return (
      <a href={destination} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  }

  return (
    <a href={destination} className={className}>
      {inner}
    </a>
  );
}

export default MarketClose;
